import { Button } from '@bambi/component-library/src/components/Button';
import type { DateRange } from '@bambi/component-library/src/components/inputs/DatePicker/DatePicker';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';
import { DateRangeFilter } from 'src/common/date/DateRangeFilter';
import { useLazyGetTilledPayoutsExportCsvQuery } from 'src/common/external/bambi-api/emptyApi';
import type { ModalProps } from 'src/common/primitives/Modal';
import { Modal } from 'src/common/primitives/Modal';
import { ModalTitle } from 'src/common/primitives/Modal/ModalTitle';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { InvoiceMultiSelectFilter } from 'src/features/billing/payments/modals/filters/InvoiceMultiSelectFilter';

export function PayoutsExportModal({
  open,
  setOpen,
}: Pick<ModalProps, 'open' | 'setOpen'>) {
  const dispatch = useAppDispatch();
  const [trigger, { isLoading }] = useLazyGetTilledPayoutsExportCsvQuery();

  const [selectedInvoices, setSelectedInvoices] = useState<SelectOption[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const handleExport = useCallback(async () => {
    try {
      await trigger({
        paid_at_after: dateRange.from
          ? DateTime.fromJSDate(dateRange.from).toISO()
          : undefined,
        paid_at_before: dateRange.to
          ? DateTime.fromJSDate(dateRange.to).endOf('day').toISO()
          : undefined,
        selectedInvoices: selectedInvoices.length
          ? selectedInvoices.map((n) => n.value).join(',')
          : undefined,
      }).unwrap();

      setOpen(false);
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Failed to export data',
          description: formatServerError(e),
        })
      );
    }
  }, [selectedInvoices, dateRange, trigger, dispatch, setOpen]);

  return (
    <Modal open={open} setOpen={setOpen} contentClassnames="max-w-md">
      <div className="flex flex-col gap-4">
        <ModalTitle>Payouts Export</ModalTitle>
        <p className="text-md">Download the list of payouts as a CSV file.</p>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Payout Date range
              </label>
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Invoice filter
              </label>
              <InvoiceMultiSelectFilter
                selected={selectedInvoices}
                onChange={setSelectedInvoices}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleExport} loading={isLoading}>
            Export
          </Button>
        </div>
      </div>
    </Modal>
  );
}
